import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";

interface ModalProps {
  show: boolean;
  onClose: () => void;
  description: React.ReactNode;
  footerButtons?: React.ReactNode[];
  gallery: React.ReactNode;
  size?: "small" | "medium" | "large" | "extra-large" | "fullscreen";
  action?: (event: React.FormEvent<HTMLFormElement>) => void;
  /** Element the modal grows out of (the clicked card's image). */
  origin?: HTMLElement | null;
}

const Modal: React.FC<ModalProps> = ({
  show,
  onClose,
  description,
  footerButtons,
  gallery,
  size = "medium",
  action,
  origin,
}) => {
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const closing = useRef(false);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  // Opening: the card image flies from its place to fill the screen, then the content rises in.
  useLayoutEffect(() => {
    if (!show || !root.current || !panel.current) return;
    closing.current = false;
    const reveal = root.current.querySelectorAll("[data-modal-reveal]");
    const tl = gsap.timeline();
    gsap.set(root.current, { clipPath: "inset(0% 0% 0% 0%)" });
    gsap.set(reveal, { opacity: 0, y: 40 });

    if (origin && origin.isConnected) {
      const from = origin.getBoundingClientRect();
      const clone = origin.cloneNode(true) as HTMLElement;
      clone.setAttribute("data-modal-clone", "");
      Object.assign(clone.style, {
        position: "fixed",
        left: `${from.left}px`,
        top: `${from.top}px`,
        width: `${from.width}px`,
        height: `${from.height}px`,
        margin: "0",
        maxWidth: "none",
        zIndex: "101",
        borderRadius: "16px",
        objectFit: getComputedStyle(origin).objectFit,
        background: "#181C20",
        pointerEvents: "none",
      });
      document.body.appendChild(clone);
      gsap.set(panel.current, { opacity: 0 });
      tl.to(clone, {
        left: 0,
        top: 0,
        width: window.innerWidth,
        height: window.innerHeight,
        borderRadius: 0,
        duration: 1.05,
        ease: "expo.inOut",
      })
        .to(panel.current, { opacity: 1, duration: 0.5, ease: "power2.out" }, "-=0.2")
        .to(clone, { opacity: 0, duration: 0.45, ease: "power2.out", onComplete: () => clone.remove() }, "<");
    } else {
      tl.fromTo(panel.current, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: "power2.out" });
    }
    tl.to(reveal, { opacity: 1, y: 0, duration: 1, stagger: 0.08, ease: "expo.out" }, "-=0.3");

    return () => {
      tl.kill();
      document.querySelectorAll("[data-modal-clone]").forEach((n) => n.remove());
    };
  }, [show, origin]);

  // Closing: content lifts away and the modal wipes upward, then the parent unmounts it.
  const requestClose = useCallback(() => {
    if (closing.current || !root.current) return;
    closing.current = true;
    const reveal = root.current.querySelectorAll("[data-modal-reveal]");
    gsap
      .timeline({
        onComplete: () => {
          closing.current = false;
          onClose();
        },
      })
      .to(reveal, { opacity: 0, y: -30, duration: 0.35, stagger: 0.04, ease: "power2.in" })
      .to(root.current, { clipPath: "inset(0% 0% 100% 0%)", duration: 0.85, ease: "expo.inOut" }, 0.15);
  }, [onClose]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && requestClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, requestClose]);

  if (!show) return null;

  const sizeClass =
    size === "small"
      ? "max-w-sm"
      : size === "large"
        ? "max-w-3xl"
        : size === "extra-large"
          ? "max-w-6xl"
          : size === "fullscreen"
            ? "w-full h-full"
            : "max-w-lg";

  const isFullscreen = size === "fullscreen";

  return (
    <div
      ref={root}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
      tabIndex={-1}
      role="dialog"
    >
      <div
        ref={panel}
        className={`relative ${isFullscreen ? 'w-full h-full' : `w-full ${sizeClass}`}`}
        role="document"
      >
        <div className="fixed z-[1] right-0 top-0 p-[20px] ml-auto ">
          <button
            type="button"
            className="bg-transparent hover:bg-white hover:scale-115 transition-all duration-[400ms] border border-[#707070] rounded-md text-black float-right text-3xl leading-none outline-none focus:outline-none"
            aria-label="Close"
            onClick={requestClose}
          >
            <span className="flex justify-center items-center h-6 w-6 text-2xl block outline-none focus:outline-none text-white hover:text-gray-500">×</span>
          </button>
        </div>
        <div className={`relative w-full bg-[#05060A] border-0 shadow-lg overflow-y-auto custom-scrollbar ${isFullscreen ? 'h-full' : 'rounded-lg max-h-[90vh]'}`}>
          <div className="flex flex-col lg:grid lg:grid-cols-[440px_1fr] w-full lg:h-full">
            <div data-modal-reveal className="lg:overflow-y-auto items-start justify-between flex-shrink-0 custom-scrollbar">
              {description}
            </div>

            <div data-modal-reveal className="lg:overflow-y-auto custom-scrollbar">
              {gallery}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
